import React, { createContext, useContext, useMemo, useReducer } from 'react'

const createEmptyCrawlItem = (overrides = {}, searchTermNumber = 1) => {
  const { searchTermNumber: _removed, ...safeOverrides } = overrides
  return {
    id: `crawl-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    start_date: null,
    end_date: null,
    searchTerms: '',
    pincodes: [],
    autoRenewal: true,
    duration: 'daily',
    skipWeekends: true,
    timeSlots: ['10:00'],
    crawlTotalAmount: 0,
    gstPercentage: 18,
    gstAmount: 0,
    totalAmount: 0,
    monthType: 'same',
    isValid: false,
    ...safeOverrides,
    searchTermNumber,
  }
}

const getInitialState = () => ({
  cards: [createEmptyCrawlItem({}, 1)],
  checkoutDataMap: {},
  existingSearchTerms: [],
  walletRefreshTrigger: 0,
  walletBalance: 0,
})

const sanitizeCheckoutData = (item) => {
  if (!item || typeof item !== 'object') {
    return item
  }

  const sanitized = { ...item }

  if (sanitized.autoRenewal) {
    delete sanitized.end_date
  }

  if (sanitized.duration === 'daily') {
    delete sanitized.date
    delete sanitized.days
    delete sanitized.monthType
  }

  if (sanitized.duration === 'weekly') {
    delete sanitized.date
    delete sanitized.monthType
    delete sanitized.skipWeekends
  }

  if (sanitized.duration === 'monthly') {
    delete sanitized.days
    delete sanitized.skipWeekends
  }

  return sanitized
}

const RankingDashboardContext = createContext(null)

const reducer = (state, action) => {
  switch (action.type) {
    case 'ADD_CARD': {
      const maxSearchTermNumber = state.cards.length > 0
        ? Math.max(...state.cards.map(card => card.searchTermNumber || 0))
        : 0
      const newSearchTermNumber = maxSearchTermNumber + 1
      return {
        ...state,
        cards: [createEmptyCrawlItem(action.payload, newSearchTermNumber), ...state.cards],
      }
    }
    case 'UPDATE_CARD': {
      const { id, data } = action.payload
      return {
        ...state,
        cards: state.cards.map(card => (card.id === id ? { ...card, ...data } : card)),
      }
    }
    case 'DELETE_CARD': {
      const { id } = action.payload
      const { [id]: _removed, ...restCheckout } = state.checkoutDataMap
      return {
        cards: state.cards.filter(card => card.id !== id),
        checkoutDataMap: restCheckout,
        existingSearchTerms: state.existingSearchTerms,
      }
    }
    case 'SET_CHECKOUT': {
      const { id, data } = action.payload
      const sanitizedData = sanitizeCheckoutData(data)
      return {
        ...state,
        checkoutDataMap: {
          ...state.checkoutDataMap,
          [id]: sanitizedData,
        },
        cards: state.cards.map(card => (card.id === id ? { ...card, ...sanitizedData, isValid: true } : card)),
      }
    }
    case 'CLEAR_CHECKOUT': {
      const { id } = action.payload
      const { [id]: _removed, ...restCheckout } = state.checkoutDataMap
      return {
        ...state,
        checkoutDataMap: restCheckout,
      }
    }
    case 'RESET_ALL':
      return getInitialState()
    case 'SET_EXISTING_SEARCH_TERMS': {
      const { terms } = action.payload
      return {
        ...state,
        existingSearchTerms: terms,
      }
    }
    case 'REFRESH_WALLET':
      return {
        ...state,
        walletRefreshTrigger: state.walletRefreshTrigger + 1,
      }
    case 'SET_WALLET_BALANCE':
      return {
        ...state,
        walletBalance: action.payload,
      }
    default:
      return state
  }
}

export const RankingDashboardProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, undefined, getInitialState)

  const value = useMemo(() => {
    const addCard = (overrides) => dispatch({ type: 'ADD_CARD', payload: overrides })
    const updateCard = (id, data) => dispatch({ type: 'UPDATE_CARD', payload: { id, data } })
    const deleteCard = (id) => dispatch({ type: 'DELETE_CARD', payload: { id } })
    const setCheckout = (id, data) => dispatch({ type: 'SET_CHECKOUT', payload: { id, data } })
    const clearCheckout = (id) => dispatch({ type: 'CLEAR_CHECKOUT', payload: { id } })
    const resetAll = () => dispatch({ type: 'RESET_ALL' })
    const setExistingSearchTerms = (terms) => dispatch({ type: 'SET_EXISTING_SEARCH_TERMS', payload: { terms } })
    const refreshWallet = () => dispatch({ type: 'REFRESH_WALLET' })
    const setWalletBalance = (balance) => dispatch({ type: 'SET_WALLET_BALANCE', payload: balance })

    return {
      cards: state.cards,
      checkoutDataMap: state.checkoutDataMap,
      existingSearchTerms: state.existingSearchTerms,
      walletRefreshTrigger: state.walletRefreshTrigger,
      walletBalance: state.walletBalance,
      addCard,
      updateCard,
      deleteCard,
      setCheckout,
      clearCheckout,
      resetAll,
      setExistingSearchTerms,
      refreshWallet,
      setWalletBalance,
    }
  }, [state.cards, state.checkoutDataMap, state.existingSearchTerms, state.walletRefreshTrigger, state.walletBalance])

  return (
    <RankingDashboardContext.Provider value={value}>
      {children}
    </RankingDashboardContext.Provider>
  )
}

export const useRankingDashboardContext = () => {
  const context = useContext(RankingDashboardContext)
  if (!context) {
    throw new Error('useRankingDashboardContext must be used within a RankingDashboardProvider')
  }
  return context
}

export { createEmptyCrawlItem }

